import type { SyncPhase } from '../state/useSync'

function ago(at: number | null): string {
  if (!at) return 'not yet'
  const seconds = Math.round((Date.now() - at) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/**
 * Signed out, this is a plain invitation to sign in — the only accented control
 * in the rail, because everything else is reachable without an account and this
 * is not. Signed in, it becomes a quiet status line.
 *
 * Offline is reported as a neutral fact rather than an error: on a phone it is
 * the ordinary case and the app is working exactly as designed. Unsent work is
 * what deserves emphasis, so the pending count gets it.
 */
export function SyncBadge({
  phase,
  pendingCount,
  lastSyncAt,
  signedIn,
  accountEmail,
  onSignIn,
  onOpenSettings,
  onSignOut,
}: {
  phase: SyncPhase
  pendingCount: number
  lastSyncAt: number | null
  signedIn: boolean
  accountEmail: string | null
  onSignIn: () => void
  onOpenSettings: () => void
  onSignOut: () => void
}) {
  if (!signedIn) {
    return (
      <button type="button" className="rail-signin" onClick={onSignIn} aria-label="Sign in">
        <span className="rail-signin-label">
          Sign in
          <span className="rail-signin-note">to sync your islands</span>
        </span>
        <span aria-hidden="true">→</span>
      </button>
    )
  }

  const label =
    phase === 'syncing'
      ? 'syncing…'
      : phase === 'offline'
        ? 'offline'
        : phase === 'error'
          ? 'sync failed'
          : `synced ${ago(lastSyncAt)}`

  const tone = phase === 'error' ? 'is-due' : phase === 'offline' ? 'is-warming' : 'is-ready'

  return (
    <div className="rail-account">
      <button
        type="button"
        className="rail-account-main"
        onClick={onOpenSettings}
        title="Sync settings"
      >
        <span style={{ minWidth: 0 }}>
          <span className="rail-account-email">{accountEmail ?? 'signed in'}</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>{label}</span>
        </span>
        {pendingCount > 0 ? (
          <span className="badge is-warming">{pendingCount}</span>
        ) : (
          <span className={`badge ${tone}`} aria-label={label} />
        )}
      </button>

      {/* One click, where the account already is. It used to live three steps
          down inside Settings, which is not where anyone looks for it. */}
      <button type="button" className="rail-signout" onClick={onSignOut}>
        Sign out
      </button>
    </div>
  )
}
